import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send, Receipt, Store, Package, ChevronRight, ArrowDownLeft, ArrowUpRight, ShoppingBag, Eye, UtensilsCrossed, Car, Home as HomeIcon, HeartPulse, Banknote } from 'lucide-react';
import { useGDLData } from './useGDLData';
import { AnimatedBalance, ProgressBar, Badge } from './GDLComponents';
import { ParticleReveal, CrossSellModal } from './AtomParticleReveal';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BENEFIT_TOTAL = 93000;
const BENEFITS = [
  { key: 'food', label: 'Food', Icon: UtensilsCrossed, alloc: 25000, color: '#10B981' },
  { key: 'transport', label: 'Transport', Icon: Car, alloc: 18000, color: '#3B82F6' },
  { key: 'housing', label: 'Housing', Icon: HomeIcon, alloc: 35000, color: '#F59E0B' },
  { key: 'hmo', label: 'HMO', Icon: HeartPulse, alloc: 15000, color: '#EF4444' },
];

export default function GDLHome() {
  const nav = useNavigate();
  const { wallet, ledger, billsHistory, csId, refresh } = useGDLData();
  const [suggestions, setSuggestions] = useState([]);
  const [crosssell, setCrosssell] = useState(null);

  // Load Atom suggestions
  useEffect(() => {
    if (!csId) return;
    axios.get(`${API}/api/demo/gdl/atom/suggestions/${csId}`, { withCredentials: true })
      .then(r => setSuggestions(r.data || []))
      .catch(() => setSuggestions([]));
  }, [csId, ledger?.length]);

  const handleAccept = async (s) => {
    // Mark suggestion accepted server-side first
    try {
      await axios.post(`${API}/api/demo/gdl/atom/suggestions/${s.id}/accept`,
        {}, { withCredentials: true });
    } catch (e) { /* still try the action */ }

    const action = s.cta_action;
    const p = s.cta_payload || {};
    try {
      if (action === 'open_vault_lock') {
        await axios.post(`${API}/api/demo/gdl/atom/vault-lock/${csId}`,
          { amount: p.amount, days: p.days || 30, rate: p.rate || 0.14,
            label: p.label || 'Savings vault' },
          { withCredentials: true });
      } else if (action === 'open_mmf_invest') {
        await axios.post(`${API}/api/demo/gdl/invest/${csId}`,
          { fund_name: p.fund_name || 'GDL Money Market Fund',
            amount: p.amount },
          { withCredentials: true });
      } else if (action === 'open_loan_apply') {
        nav('/demo/gdl/loan/apply');
        return;
      }
      setSuggestions(prev => prev.filter(x => x.id !== s.id));
      refresh();
    } catch (e) {
      setSuggestions(prev => prev.filter(x => x.id !== s.id));
    }
  };

  const handleDismiss = async (s) => {
    try {
      await axios.post(`${API}/api/demo/gdl/atom/suggestions/${s.id}/dismiss`,
        {}, { withCredentials: true });
    } catch (e) { /* swallow */ }
    setSuggestions(prev => prev.filter(x => x.id !== s.id));
  };

  if (!wallet) return <Skeleton />;

  const { civil_servant: cs, wallet: w, loan } = wallet;

  // Benefit spending
  let totalSpent = 0;
  const catSpent = {};
  (billsHistory || []).forEach(b => {
    const cat = (b.category || '').toLowerCase();
    if (cat.includes('feed') || cat.includes('food')) { catSpent.food = (catSpent.food || 0) + b.amount; totalSpent += b.amount; }
    else if (cat.includes('transport')) { catSpent.transport = (catSpent.transport || 0) + b.amount; totalSpent += b.amount; }
    else if (cat.includes('hous')) { catSpent.housing = (catSpent.housing || 0) + b.amount; totalSpent += b.amount; }
    else if (cat.includes('hmo') || cat.includes('health')) { catSpent.hmo = (catSpent.hmo || 0) + b.amount; totalSpent += b.amount; }
  });
  const benefitRemaining = Math.max(BENEFIT_TOTAL - totalSpent, 0);

  const loanPct = loan && loan.principal > 0
    ? ((loan.principal - loan.outstanding_balance) / loan.principal) * 100 : 0;

  return (
    <div style={{ padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Balance Card (Wine gradient like reference yellow card) ── */}
      <div data-testid="balance-hero" style={{
        borderRadius: 24, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(145deg, #C43B4D 0%, #B03140 40%, #8E2233 100%)',
        boxShadow: '0 8px 32px rgba(176,49,64,0.3)',
      }}>
        {/* Decorative elements */}
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 130, height: 130,
          borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -20, left: -20, width: 80, height: 80,
          borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none',
        }} />
        <div style={{ padding: '24px 22px 20px', position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 500, letterSpacing: '0.04em' }}>NGN</div>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Eye size={14} color="rgba(255,255,255,0.7)" />
            </div>
          </div>
          <div style={{
            fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2,
          }}>
            {cs.account_number} {'\u00B7'} {cs.bank_name}
          </div>
          <div style={{ fontSize: 34, fontWeight: 700, color: '#fff', marginTop: 8, letterSpacing: '-0.03em' }}>
            <AnimatedBalance value={w.spendable_balance} prefix={'\u20A6'} />
          </div>
          <div style={{
            display: 'inline-block', marginTop: 6,
            fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 500,
          }}>
            Available Balance
          </div>
        </div>
      </div>

      {/* ── Quick Actions (Circular like reference) ───────────── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
        {[
          { icon: Send, label: 'Send', to: '/demo/gdl/wallet' },
          { icon: Receipt, label: 'Bills', to: '/demo/gdl/bills' },
          { icon: Store, label: 'Vendors', to: '/demo/gdl/vendors' },
          { icon: Banknote, label: 'Loan', to: '/demo/gdl/loan/apply' },
        ].map(a => (
          <button key={a.label} onClick={() => nav(a.to)} data-testid={`quick-${a.label.toLowerCase()}`}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            <div style={{
              width: 52, height: 52, borderRadius: '50%', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 12px rgba(176,49,64,0.1)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}>
              <a.icon size={20} color="#B03140" />
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#8E2233' }}>{a.label}</span>
          </button>
        ))}
      </div>

      {/* ── Atom suggestions (Moment 01: salary lands) ─────────── */}
      {suggestions.length > 0 && (
        <div data-testid="atom-suggestions" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {suggestions.slice(0, 3).map(s => (
            <ParticleReveal
              key={s.id}
              testid={`atom-suggestion-${s.kind}`}
              headline={s.headline}
              body={s.body}
              ctaLabel={s.cta_label}
              onCta={() => handleAccept(s)}
              onDismiss={() => handleDismiss(s)}
              particles={s.particles}
            />
          ))}
        </div>
      )}

      {/* ── Latest Transactions ──────────────────── */}
      <div style={{ background: '#fff', borderRadius: 20, padding: '16px 18px', boxShadow: '0 2px 12px rgba(176,49,64,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>Latest Transactions</div>
          <button onClick={() => nav('/demo/gdl/wallet')} style={{
            background: 'none', border: 'none', color: '#B03140', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            See All
          </button>
        </div>
        {(!ledger || ledger.length === 0) && (
          <div style={{ textAlign: 'center', color: '#A07078', padding: 24, fontSize: 14 }}>No transactions yet</div>
        )}
        {(ledger || []).slice(0, 4).map((tx, idx) => {
          const isCredit = tx.amount >= 0;
          const isVendor = tx.type === 'vendor' || tx.type === 'bill';
          const iconBg = isVendor ? 'rgba(176,49,64,0.08)' : isCredit ? '#ECFDF5' : '#FEF2F2';
          const iconColor = isVendor ? '#B03140' : isCredit ? '#10B981' : '#EF4444';
          const Icon = isVendor ? ShoppingBag : isCredit ? ArrowDownLeft : ArrowUpRight;
          return (
            <div key={tx.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0',
              borderBottom: idx < Math.min((ledger || []).length - 1, 3) ? '1px solid rgba(176,49,64,0.05)' : 'none',
            }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={16} color={iconColor} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tx.description || tx.type}
                </div>
                <div style={{ fontSize: 11, color: '#A07078', marginTop: 1 }}>{new Date(tx.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: isCredit ? '#10B981' : '#2D0A10', flexShrink: 0 }}>
                {isCredit ? '+' : '-'}{'\u20A6'}{Math.abs(tx.amount).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── GDL+ Benefits — Horizontal Scroll ─── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: '0 2px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>GDL+ Benefits</div>
          <button onClick={() => nav('/demo/gdl/benefits')} style={{
            background: 'none', border: 'none', color: '#B03140', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            See all <ChevronRight size={14} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
          {/* Total pool card */}
          <div style={{
            minWidth: 140, borderRadius: 18, padding: '18px 16px',
            background: 'linear-gradient(145deg, #C43B4D, #8E2233)', color: '#fff', flexShrink: 0,
            boxShadow: '0 4px 16px rgba(176,49,64,0.25)',
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Pool</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{'\u20A6'}{benefitRemaining.toLocaleString()}</div>
            <div style={{ fontSize: 10, opacity: 0.4, marginTop: 2 }}>of {'\u20A6'}{BENEFIT_TOTAL.toLocaleString()}</div>
            <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.15)', marginTop: 10 }}>
              <div style={{ height: '100%', borderRadius: 2, background: '#fff', width: `${(benefitRemaining / BENEFIT_TOTAL) * 100}%`, transition: 'width 0.6s ease' }} />
            </div>
          </div>
          {/* Category cards */}
          {BENEFITS.map(b => {
            const spent = catSpent[b.key] || 0;
            const rem = Math.max(b.alloc - spent, 0);
            const BIcon = b.Icon;
            return (
              <div key={b.key} data-testid={`benefit-${b.key}`} style={{
                minWidth: 120, borderRadius: 18, padding: '18px 14px',
                background: '#fff', flexShrink: 0,
                boxShadow: '0 2px 8px rgba(176,49,64,0.06)',
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${b.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BIcon size={18} color={b.color} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#2D0A10', marginTop: 6 }}>{b.label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10', marginTop: 2 }}>{'\u20A6'}{rem.toLocaleString()}</div>
                <div style={{ height: 3, borderRadius: 2, background: 'rgba(176,49,64,0.08)', marginTop: 8 }}>
                  <div style={{ height: '100%', borderRadius: 2, background: b.color, width: `${b.alloc > 0 ? (rem / b.alloc) * 100 : 0}%`, transition: 'width 0.6s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Loan Summary ────────────────── */}
      {loan && loan.status !== 'none' && (
        <div style={{
          background: 'linear-gradient(145deg, #C43B4D 0%, #B03140 50%, #8E2233 100%)',
          borderRadius: 20, padding: '20px', overflow: 'hidden', position: 'relative',
          boxShadow: '0 4px 20px rgba(176,49,64,0.25)',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, position: 'relative' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>GDL Loan</div>
            <div style={{ padding: '4px 12px', borderRadius: 999, background: loan.status === 'active' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.15)', fontSize: 11, fontWeight: 600, color: loan.status === 'active' ? '#6EE7B7' : 'rgba(255,255,255,0.7)' }}>
              {loan.status === 'active' ? 'Active' : loan.status}
            </div>
          </div>
          {/* Outstanding - large */}
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Outstanding Balance</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', marginTop: 4, letterSpacing: '-0.02em' }}>{'\u20A6'}{loan.outstanding_balance.toLocaleString()}</div>
          </div>
          {/* Monthly + Paid side by side */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14, position: 'relative' }}>
            <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Monthly</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginTop: 4 }}>{'\u20A6'}{loan.monthly_repayment.toLocaleString()}</div>
            </div>
            <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Months Paid</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#6EE7B7', marginTop: 4 }}>{loan.months_paid} of 12</div>
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ position: 'relative' }}>
            <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 3, background: '#6EE7B7', width: `${loanPct}%`, transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{Math.round(loanPct)}% repaid</span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{'\u20A6'}{loan.principal?.toLocaleString()} total</span>
            </div>
          </div>
        </div>
      )}

      <CrossSellModal
        suggestion={crosssell}
        onAccept={async (s) => { await handleAccept({ ...s, id: 'inline' }); setCrosssell(null); }}
        onDismiss={() => setCrosssell(null)}
      />
    </div>
  );
}

/* ── Skeleton loader ───────────────── */
function Skeleton() {
  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="gdl-skeleton" style={{ width: '100%', height: 160, borderRadius: 24 }} />
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
        {[1,2,3,4].map(i => <div key={i} className="gdl-skeleton" style={{ width: 52, height: 52, borderRadius: '50%' }} />)}
      </div>
      <div className="gdl-skeleton" style={{ width: '100%', height: 200, borderRadius: 20 }} />
      <div style={{ display: 'flex', gap: 10 }}>{[1,2,3].map(i => <div key={i} className="gdl-skeleton" style={{ width: 130, height: 100, borderRadius: 18 }} />)}</div>
    </div>
  );
}
